import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ISeezFinanceCalculator } from '@ncg/data';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { FeatureDetectionService } from '../../../core/feature-detection.service';
import { SeezService } from '../../../core/seez.service';
import { TrackingService } from '../../../core/tracking.service';
import { ProductPageService } from '../../../page/product-page/product-page.service';
import { DialogService } from '../../../utils/dialog/dialog.service';
import { SeezFinanceCalculatorComponent } from '../seez-finance-calculator/seez-finance-calculator.component';

@Component({
    selector: 'ncg-seez-finance-calculator-button',
    standalone: true,
    template: `
        <button type="button" *ngIf="showButton" [ngClass]="buttonClass" (click)="onClick()">
            {{ buttonText || ('product_details.finance_calculator_button' | translate) }}
        </button>
    `,
    imports: [CommonModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeezFinanceCalculatorButtonComponent implements OnInit, OnDestroy {
    private listingId?: string;

    public showButton?: boolean;

    @Input()
    public buttonClass?: string;

    @Input()
    public buttonText?: string;

    @Input()
    public data?: ISeezFinanceCalculator;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly el: ElementRef,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly productPageService: ProductPageService,
        private readonly seezService: SeezService,
        private readonly dialogService: DialogService,
        private readonly trackingService: TrackingService
    ) {}

    public ngOnDestroy(): void {
        const trackingElement = this.getTrackingElement();
        if (trackingElement) {
            trackingElement.removeEventListener('click', this.onFinanceCalculatorButtonClick);
        }
    }

    public ngOnInit(): void {
        if (this.featureDetectionService.isBrowser()) {
            this.loadSeezSdk().catch(console.error);
        }
    }

    public onClick() {
        if (this.data) {
            this.dialogService.openDialogWithComponent(SeezFinanceCalculatorComponent, {
                data: this.data,
            });
        }
    }

    private async loadSeezSdk() {
        const { product, dealership } = await firstValueFrom(this.productPageService.productPageData$);
        const carBusinessId = dealership?.location.business?.identifier;
        const productId = product?.properties?.id;

        if (productId && carBusinessId) {
            this.seezService.initSeezSdk(carBusinessId).then(async (isLoaded) => {
                if (isLoaded) {
                    this.listingId = await this.seezService.getSeezListingId(productId);
                    this.showButton = Boolean(this.listingId);
                }
                this.cd.markForCheck();
                const trackingElement = this.getTrackingElement();
                if (trackingElement) {
                    trackingElement.addEventListener('click', this.onFinanceCalculatorButtonClick);
                }
            });
        }
    }

    private getTrackingElement(): HTMLButtonElement | undefined | null {
        return this.el?.nativeElement?.querySelector('ncg-seez-finance-calculator-button button');
    }

    private readonly onFinanceCalculatorButtonClick = () => {
        this.trackingService.trackSeez('finance calculator button click');
    };
}
